// screens/Systems.tsx — Chunk SYS-W, plate "Systems": who is working on
// what within what limits — agents, meters, fleet, doors, the compliance
// calendar, recovery evidence. Every row's history opens in a side sheet
// (FactHistorySheet, full screen on the phone by its own media query). A
// state is always a word (never colour alone); a stale fact says "stale
// since <time>"; a meter at or near its cap says so in words too.
import { useCallback, useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  addComplianceFact,
  closeComplianceFact,
  getSystemsSummary,
  type SystemFactApi,
  type SystemSection,
  type SystemsSummaryApi,
} from '../lib/api';
import { FactHistorySheet } from '../components/FactHistorySheet';
import './Systems.css';

export interface SystemsProps {
  tenantId: string;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; summary: SystemsSummaryApi };

function when(at: string): string {
  return new Date(at).toLocaleString('en-CA', {
    timeZone: 'America/Toronto',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const MONTH_WORD = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `dueOn` is a plain YYYY-MM-DD date, never a timestamp — formatted from
 * its own digits, never through `Date`, which would read it as midnight
 * UTC and print the day before in America/Toronto. */
function dueWhen(dueOn: string): string {
  const [, , month, day] = dueOn.match(/^(\d{4})-(\d{2})-(\d{2})$/) ?? [];
  if (!month || !day) return dueOn;
  return `${MONTH_WORD[Number(month) - 1]} ${Number(day)}`;
}

function meterLine(fact: SystemFactApi): string | null {
  if (fact.valueNum === null) return null;
  const value = Number.isInteger(fact.valueNum) ? String(fact.valueNum) : fact.valueNum.toFixed(1);
  if (fact.capNum === null) return fact.unit ? `${value} ${fact.unit}` : value;
  const cap = Number.isInteger(fact.capNum) ? String(fact.capNum) : fact.capNum.toFixed(1);
  const unit = fact.unit ? ` ${fact.unit}` : '';
  return `${value} of ${cap}${unit}`;
}

/** Near-cap, at-cap and stale words — the only ones a meter row ever shows
 * beside its own value (never the fact's bare `state`, which a reporter
 * sends for its own bookkeeping, e.g. 'green'/'none', meaningless on a
 * meter — SYS-W fix round, item B). */
function meterWords(fact: SystemFactApi): string[] {
  const words: string[] = [];
  if (fact.meter === 'near') words.push('near its cap');
  if (fact.meter === 'at_cap') words.push('at its cap');
  if (fact.stale && fact.staleSince) words.push(`stale since ${when(fact.staleSince)}`);
  return words;
}

interface FactRowProps {
  fact: SystemFactApi;
  onOpen: () => void;
}

/** Agents, Fleet, Doors, Recovery — label, the state word, the detail, any
 * due date, and "stale since <time>" when it applies. Never a meter line:
 * only the meters section's own MeterRow reads valueNum/capNum. */
function FactRow({ fact, onOpen }: FactRowProps) {
  const warn = fact.stale;
  return (
    <button type="button" class="systems__fact" onClick={onOpen}>
      <div class="systems__fact-top">
        <span class="systems__fact-label">{fact.label}</span>
        <span class={`systems__fact-state${warn ? ' systems__fact-state--warn' : ''}`}>
          {fact.state.replace(/_/g, ' ')}
        </span>
      </div>
      {fact.detail ? <div class="systems__fact-detail">{fact.detail}</div> : null}
      {fact.dueOn ? <div class="systems__fact-due">due {dueWhen(fact.dueOn)}</div> : null}
      {fact.stale && fact.staleSince ? <div class="systems__fact-stale">stale since {when(fact.staleSince)}</div> : null}
    </button>
  );
}

/** Limits, as meters — label · "<value> of <cap> <unit>" · and only when it
 * applies, near/at-cap or stale, in words. The fact's own `state` (a
 * reporter's bookkeeping word, not a meter reading) never appears here. */
function MeterRow({ fact, onOpen }: FactRowProps) {
  const line = meterLine(fact);
  const words = meterWords(fact);
  const warn = words.length > 0;
  return (
    <button type="button" class="systems__fact" onClick={onOpen}>
      <div class="systems__fact-top">
        <span class="systems__fact-label">{fact.label}</span>
      </div>
      {line ? (
        <div class={`systems__fact-meter${warn ? ' systems__fact-meter--warn' : ''}`}>
          {line}
          {words.length ? ` · ${words.join(' · ')}` : ''}
        </div>
      ) : null}
    </button>
  );
}

function GhostRows({ n }: { n: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: n }).map((_, i) => (
        <div class="systems__ghost" key={i}>
          <div class="systems__ghost-line systems__ghost-line--wide" />
          <div class="systems__ghost-line systems__ghost-line--narrow" />
        </div>
      ))}
    </div>
  );
}

export function Systems({ tenantId }: SystemsProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [history, setHistory] = useState<{ section: SystemSection; key: string; label: string } | null>(null);
  const [closingKey, setClosingKey] = useState<string | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [addLabel, setAddLabel] = useState('');
  const [addDueOn, setAddDueOn] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const load_ = useCallback(() => {
    setLoad({ phase: 'loading' });
    getSystemsSummary(tenantId)
      .then((summary) => setLoad({ phase: 'ready', summary }))
      .catch((err: unknown) =>
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? (err.serverMessage ?? err.message) : 'systems could not be read',
        })
      );
  }, [tenantId]);

  useEffect(() => {
    load_();
  }, [load_]);

  async function onCloseCompliance(key: string) {
    setClosingKey(key);
    setCloseError(null);
    try {
      await closeComplianceFact(tenantId, key);
      load_();
    } catch (err) {
      setCloseError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'that item could not be closed');
    } finally {
      setClosingKey(null);
    }
  }

  async function onAddCompliance(e: Event) {
    e.preventDefault();
    if (!addLabel.trim()) return;
    setAddBusy(true);
    setAddError(null);
    try {
      await addComplianceFact(tenantId, { label: addLabel.trim(), dueOn: addDueOn || null });
      setAddLabel('');
      setAddDueOn('');
      load_();
    } catch (err) {
      setAddError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'that item could not be opened');
    } finally {
      setAddBusy(false);
    }
  }

  if (load.phase === 'loading') {
    return <div class="systems" aria-hidden="true" />;
  }

  if (load.phase === 'error') {
    return (
      <div class="systems">
        <div class="systems__say">{load.message}</div>
      </div>
    );
  }

  const { summary } = load;
  const openSection = (section: SystemSection, fact: SystemFactApi) => setHistory({ section, key: fact.key, label: fact.label });

  return (
    <div class="systems">
      <h1 class="systems__title">Systems</h1>

      <div class="systems__row2">
        <section class="systems__section">
          <h2 class="systems__section-h">Agents</h2>
          <div class="systems__list">
            {summary.sections.agents.length === 0 ? (
              <GhostRows n={2} />
            ) : (
              summary.sections.agents.map((f) => (
                <FactRow key={f.key} fact={f} onOpen={() => openSection('agents', f)} />
              ))
            )}
          </div>
        </section>

        <section class="systems__section">
          <h2 class="systems__section-h">Limits, as meters</h2>
          <div class="systems__list">
            {summary.sections.meters.length === 0 ? (
              <GhostRows n={2} />
            ) : (
              summary.sections.meters.map((f) => (
                <MeterRow key={f.key} fact={f} onOpen={() => openSection('meters', f)} />
              ))
            )}
          </div>
        </section>
      </div>

      <div class="systems__row3">
        <section class="systems__section">
          <h2 class="systems__section-h">Fleet</h2>
          <div class="systems__list">
            {summary.sections.fleet.length === 0 ? (
              <GhostRows n={2} />
            ) : (
              summary.sections.fleet.map((f) => (
                <FactRow key={f.key} fact={f} onOpen={() => openSection('fleet', f)} />
              ))
            )}
          </div>
        </section>

        <section class="systems__section">
          <h2 class="systems__section-h">Doors</h2>
          <div class="systems__list">
            {summary.sections.doors.length === 0 ? (
              <GhostRows n={2} />
            ) : (
              summary.sections.doors.map((f) => (
                <FactRow key={f.key} fact={f} onOpen={() => openSection('doors', f)} />
              ))
            )}
          </div>
        </section>

        <section class="systems__section">
          <h2 class="systems__section-h">Compliance calendar</h2>
          <div class="systems__list">
            {summary.sections.compliance.length === 0 ? (
              <GhostRows n={2} />
            ) : (
              summary.sections.compliance.map((f) => (
                <div class="systems__compliance-row" key={f.key}>
                  <div
                    class="systems__compliance-left"
                    role="button"
                    tabIndex={0}
                    onClick={() => openSection('compliance', f)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') openSection('compliance', f);
                    }}
                  >
                    <div class="systems__fact-label">{f.label}</div>
                    {f.dueOn ? <div class="systems__fact-due">due {dueWhen(f.dueOn)}</div> : null}
                  </div>
                  <div class="systems__compliance-right">
                    <span class="systems__fact-state">{f.state.replace(/_/g, ' ')}</span>
                    {f.state !== 'done' ? (
                      <button
                        type="button"
                        class="systems__compliance-close"
                        disabled={closingKey === f.key}
                        onClick={() => onCloseCompliance(f.key)}
                      >
                        {closingKey === f.key ? 'closing…' : 'Close'}
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
          {closeError ? <div class="systems__compliance-error">{closeError}</div> : null}
          <form class="systems__compliance-add" onSubmit={onAddCompliance}>
            <input
              type="text"
              placeholder="What is due"
              value={addLabel}
              onInput={(e) => setAddLabel((e.target as HTMLInputElement).value)}
              aria-label="What is due"
            />
            <input
              type="date"
              value={addDueOn}
              onInput={(e) => setAddDueOn((e.target as HTMLInputElement).value)}
              aria-label="Due on"
            />
            <button type="submit" class="systems__compliance-add-submit" disabled={addBusy || !addLabel.trim()}>
              Add
            </button>
            {addError ? <div class="systems__compliance-error">{addError}</div> : null}
          </form>
        </section>
      </div>

      <section class="systems__section">
        <h2 class="systems__section-h">Recovery evidence</h2>
        <div class="systems__list">
          {summary.sections.recovery.length === 0 ? (
            <GhostRows n={1} />
          ) : (
            summary.sections.recovery.map((f) => (
              <FactRow key={f.key} fact={f} onOpen={() => openSection('recovery', f)} />
            ))
          )}
        </div>
      </section>

      {/* H3b (migration 0936) — the project columns' own status facts, the
       * SAME `projects/<slug>` rows the board's top card reads
       * (lib/cockpitBoard.ts's `findProjectFact`). A plain FactRow, same
       * floor as Agents/Fleet/Doors/Recovery: label, state word, detail,
       * "stale since <time>" when it applies — never a meter line (a
       * project fact carries no cap). */}
      <section class="systems__section">
        <h2 class="systems__section-h">Projects</h2>
        <div class="systems__list">
          {summary.sections.projects.length === 0 ? (
            <GhostRows n={1} />
          ) : (
            summary.sections.projects.map((f) => (
              <FactRow key={f.key} fact={f} onOpen={() => openSection('projects', f)} />
            ))
          )}
        </div>
      </section>

      {history ? (
        <FactHistorySheet
          tenantId={tenantId}
          section={history.section}
          key_={history.key}
          label={history.label}
          onClose={() => setHistory(null)}
        />
      ) : null}
    </div>
  );
}
